import {
  HealthResponse,
  AuthResponse,
  MeResponse,
  UserListResponse,
  SignupListResponse,
  InviteLinkResponse,
  JoinHostResponse,
  JoinSubmitResponse,
  JoinConnectResponse,
  type JoinSubmitInput,
  MemberDTO,
  MemberListResponse,
  MemberResponse,
  MemberImportResult,
  MemberFieldListResponse,
  type MemberFieldDefDTO,
  type MemberFieldCreateInput,
  type MemberFieldUpdateInput,
  ImportMapResponse,
  ImportFreetextResponse,
  type ImportMapInput,
  DismissedListResponse,
  MemberExtractResponse,
  type MemberExtractInput,
  TagListResponse,
  ProfileResponse,
  SearchResponse,
  MatchListResponse,
  MatchResponse,
  MatchSuggestionsResponse,
  MatchExplainResponse,
  MapResponse,
  GeoSearchResponse,
  BackupExport,
  BackupImportResult,
  AdminAnalytics7d,
  ActivityResponse,
  AiCostsResponse,
  FeedbackListResponse,
  FeedbackDetailResponse,
  FeedbackStatsResponse,
  type FeedbackDTO,
  type FeedbackDetailDTO,
  type FeedbackCreateInput,
  type FeedbackUpdateInput,
  type FeedbackListQuery,
  type ChangePasswordInput,
  type UpdateProfileInput,
  type DeleteAccountInput,
  type BackupImportInput,
  type LoginInput,
  type RegisterInput,
  type ForgotPasswordInput,
  type ResetPasswordInput,
  type UpdateUserInput,
  type UserDTO,
  type MemberCreateInput,
  type MemberUpdateInput,
  type MemberListQuery,
  type MemberImportRow,
  type SearchQuery,
  type MatchCreateInput,
  type MatchUpdateInput,
  type MatchListQuery,
  type MatchDTO,
  type MatchExplainInput,
  type RelationshipDTO,
  type RelationshipCreateInput,
  type RelationshipUpdateInput,
  GroupListResponse,
  GroupDetailResponse,
  GroupInviteListResponse,
  GroupInviteResponse,
  GroupInvitePreviewResponse,
  GroupMemberProfileResponse,
  GroupInviteAcceptResponse,
  GroupManualInviteResponse,
  type GroupCreateInput,
  type GroupUpdateInput,
  type GroupInviteCreateInput,
  type GroupManualInviteInput,
  type GroupMemberRoleInput,
} from '@nexply/api-contract';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
  ) {
    super(message ?? code);
  }
}

async function request<T>(base: string, path: string, init: RequestInit = {}): Promise<T> {
  // Content-Type nur setzen, wenn es auch einen Body gibt. Fastify lehnt eine Anfrage mit
  // `application/json` und leerem Body mit 400 (FST_ERR_CTP_EMPTY_JSON_BODY) ab — dadurch
  // schlug bisher *jedes* DELETE der App fehl, noch vor der Auth-Prüfung.
  const headers: Record<string, string> = { ...((init.headers as Record<string, string>) ?? {}) };
  if (init.body !== undefined && init.body !== null && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const r = await fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
  const text = await r.text();
  const body = text ? JSON.parse(text) : null;
  if (!r.ok) {
    throw new ApiError(r.status, body?.error ?? 'request_failed', body?.message);
  }
  return body as T;
}

function buildQuery(q?: Record<string, unknown>): string {
  if (!q) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export interface NexplyClient {
  health(): Promise<HealthResponse>;
  auth: {
    login(input: LoginInput): Promise<{ user: UserDTO }>;
    register(input: RegisterInput): Promise<{ user: UserDTO }>;
    logout(): Promise<{ ok: true }>;
    me(): Promise<MeResponse>;
    forgotPassword(input: ForgotPasswordInput): Promise<{ ok: true }>;
    resetPassword(input: ResetPasswordInput): Promise<{ ok: true }>;
  };
  admin: {
    listUsers(): Promise<UserListResponse>;
    /** Selbst-Eintragungen über die Einladungslinks, inkl. Einwilligungen. */
    listSignups(): Promise<SignupListResponse>;
    updateUser(id: string, input: UpdateUserInput): Promise<{ user: UserDTO }>;
    deleteUser(id: string): Promise<{ ok: true }>;
  };
  members: {
    list(q?: MemberListQuery): Promise<MemberListResponse>;
    get(id: string): Promise<{ member: MemberDTO }>;
    create(input: MemberCreateInput): Promise<{ member: MemberDTO }>;
    update(id: string, input: MemberUpdateInput): Promise<{ member: MemberDTO }>;
    remove(id: string): Promise<{ ok: true }>;
    import(rows: MemberImportRow[]): Promise<MemberImportResult>;
    /** KI ordnet fremde CSV-Spalten unseren Feldern zu (Haiku-Call). */
    importMap(input: ImportMapInput): Promise<ImportMapResponse>;
    /** KI extrahiert Kontakte aus Freitext (Haiku-Call). */
    importFreetext(text: string): Promise<ImportFreetextResponse>;
    tags(): Promise<TagListResponse>;
    /** Trägt Koordinaten für Mitglieder nach, die eine Stadt, aber kein lat/lng haben. */
    geocodeMissing(): Promise<{ queued: number }>;
    /**
     * Sprachnotiz → strukturierte Felder + erkannte Verbindungen.
     * Kostet einen Sonnet-Call — nur auf Nutzerwunsch aufrufen.
     */
    extract(input: MemberExtractInput): Promise<MemberExtractResponse>;
    /** Fingerabdrücke der Kontakte, die beim Einpflegen weggeswipt wurden. */
    listDismissed(): Promise<DismissedListResponse>;
    dismiss(fingerprints: string[]): Promise<{ added: number }>;
    undismiss(fingerprints: string[]): Promise<{ removed: number }>;
    /** Eigene Felder des Nutzers — gelten für sein ganzes Netzwerk. */
    listFields(): Promise<MemberFieldListResponse>;
    createField(input: MemberFieldCreateInput): Promise<{ field: MemberFieldDefDTO }>;
    updateField(id: string, input: MemberFieldUpdateInput): Promise<{ field: MemberFieldDefDTO }>;
    removeField(id: string): Promise<{ ok: true }>;
  };
  profile: {
    me(): Promise<ProfileResponse>;
    save(input: MemberUpdateInput): Promise<{ member: MemberDTO }>;
  };
  search: {
    run(query: SearchQuery): Promise<SearchResponse>;
  };
  matches: {
    list(q?: MatchListQuery): Promise<MatchListResponse>;
    suggest(memberId: string, opts?: { ai?: boolean }): Promise<MatchSuggestionsResponse>;
    /** Ausführliche KI-Erklärung zu einem Paar. Kostet einen Sonnet-Call — nur auf Nutzerwunsch aufrufen. */
    explain(input: MatchExplainInput): Promise<MatchExplainResponse>;
    create(input: MatchCreateInput): Promise<{ match: MatchDTO }>;
    update(id: string, input: MatchUpdateInput): Promise<{ match: MatchDTO }>;
    remove(id: string): Promise<{ ok: true }>;
  };





  map: {
    get(): Promise<MapResponse>;
    /** Freitext-Ortssuche (Nominatim, deutsch) für "Standort setzen". */
    geoSearch(q: string): Promise<GeoSearchResponse>;
  };





  account: {
    updateProfile(input: UpdateProfileInput): Promise<{ user: UserDTO }>;
    /** Eigener Einladungslink — wird beim ersten Abruf erzeugt. */
    inviteLink(): Promise<InviteLinkResponse>;
    changePassword(input: ChangePasswordInput): Promise<{ ok: true }>;
    deleteAccount(input: DeleteAccountInput): Promise<void>;
    /** Markiert den Schnellstart als gesehen (abgeschlossen oder übersprungen). */
    markOnboarded(): Promise<{ user: UserDTO }>;
  };
  backup: {
    export(): Promise<BackupExport>;
    import(input: BackupImportInput): Promise<BackupImportResult>;
  };
  analytics: {
    last7Days(): Promise<AdminAnalytics7d>;
    /** Nutzeraktivität: jetzt online, aktiv heute/7d, Interaktionen pro Tag & Nutzer. */
    activity(days?: number): Promise<ActivityResponse>;
    aiCosts(days?: number): Promise<AiCostsResponse>;
  };
  join: {
    host(slug: string): Promise<JoinHostResponse>;
    submit(slug: string, input: JoinSubmitInput): Promise<JoinSubmitResponse>;
    /** Bestehendes Konto mit dem Gastgeber vernetzen — ohne Formular. */
    connect(slug: string): Promise<JoinConnectResponse>;
  };
  feedback: {
    create(input: FeedbackCreateInput): Promise<{ feedback: FeedbackDTO }>;
    mine(): Promise<FeedbackListResponse>;
    get(id: string): Promise<{ feedback: FeedbackDetailDTO }>;
    remove(id: string): Promise<{ ok: true }>;
    adminList(q?: FeedbackListQuery): Promise<FeedbackListResponse>;
    adminStats(): Promise<FeedbackStatsResponse>;
    adminUpdate(id: string, input: FeedbackUpdateInput): Promise<{ feedback: FeedbackDTO }>;
  };
  network: {
    createRelationship(input: RelationshipCreateInput): Promise<{ relationship: RelationshipDTO }>;
    updateRelationship(id: string, input: RelationshipUpdateInput): Promise<{ relationship: RelationshipDTO }>;
    removeRelationship(id: string): Promise<{ ok: true }>;
  };
  groups: {
    list(): Promise<GroupListResponse>;
    create(input: GroupCreateInput): Promise<GroupDetailResponse>;
    get(id: string): Promise<GroupDetailResponse>;
    update(id: string, input: GroupUpdateInput): Promise<GroupDetailResponse>;
    remove(id: string): Promise<{ ok: true }>;
    leave(id: string): Promise<{ ok: true }>;
    removeMember(id: string, userId: string): Promise<{ ok: true }>;
    setRole(id: string, userId: string, input: GroupMemberRoleInput): Promise<GroupDetailResponse>;
    memberProfile(id: string, userId: string): Promise<GroupMemberProfileResponse>;
    connectMemberProfile(id: string, userId: string): Promise<{ memberId: string; alreadyExists: boolean }>;
    inviteByEmail(id: string, input: GroupManualInviteInput): Promise<GroupManualInviteResponse>;
    invites(id: string): Promise<GroupInviteListResponse>;
    createInvite(id: string, input: GroupInviteCreateInput): Promise<GroupInviteResponse>;
    revokeInvite(id: string, inviteId: string): Promise<GroupInviteResponse>;
    previewInvite(token: string): Promise<GroupInvitePreviewResponse>;
    acceptInvite(token: string): Promise<GroupInviteAcceptResponse>;
  };
}

export function createClient(baseUrl: string): NexplyClient {
  const base = baseUrl.replace(/\/$/, '');
  return {
    health: async () => HealthResponse.parse(await request(base, '/health')),
    auth: {
      login: (input) => request(base, '/auth/login', { method: 'POST', body: JSON.stringify(input) }) as Promise<AuthResponse>,
      register: (input) => request(base, '/auth/register', { method: 'POST', body: JSON.stringify(input) }) as Promise<AuthResponse>,
      logout: () => request(base, '/auth/logout', { method: 'POST' }) as Promise<{ ok: true }>,
      me: async () => MeResponse.parse(await request(base, '/auth/me')),
      forgotPassword: (input) =>
        request(base, '/auth/forgot-password', { method: 'POST', body: JSON.stringify(input) }) as Promise<{ ok: true }>,
      resetPassword: (input) =>
        request(base, '/auth/reset-password', { method: 'POST', body: JSON.stringify(input) }) as Promise<{ ok: true }>,
    },
    admin: {
      listSignups: async () => SignupListResponse.parse(await request(base, '/admin/signups')),
      listUsers: async () => UserListResponse.parse(await request(base, '/admin/users')),
      updateUser: (id, input) =>
        request(base, `/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }) as Promise<{ user: UserDTO }>,
      deleteUser: (id) => request(base, `/admin/users/${id}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
    },
    members: {
      list: async (q) => MemberListResponse.parse(await request(base, `/members${buildQuery(q)}`)),
      get: async (id) => MemberResponse.parse(await request(base, `/members/${id}`)),
      create: (input) =>
        request(base, '/members', { method: 'POST', body: JSON.stringify(input) }) as Promise<{ member: MemberDTO }>,
      update: (id, input) =>
        request(base, `/members/${id}`, { method: 'PATCH', body: JSON.stringify(input) }) as Promise<{ member: MemberDTO }>,
      remove: (id) => request(base, `/members/${id}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
      import: async (rows) =>
        MemberImportResult.parse(
          await request(base, '/members/import', { method: 'POST', body: JSON.stringify({ rows }) }),
        ),
      importMap: async (input) =>
        ImportMapResponse.parse(
          await request(base, '/members/import/map', { method: 'POST', body: JSON.stringify(input) }),
        ),
      importFreetext: async (text) =>
        ImportFreetextResponse.parse(
          await request(base, '/members/import/freetext', { method: 'POST', body: JSON.stringify({ text }) }),
        ),
      tags: async () => TagListResponse.parse(await request(base, '/members/tags')),
      geocodeMissing: () =>
        request(base, '/members/geocode-missing', { method: 'POST' }) as Promise<{ queued: number }>,
      extract: async (input) =>
        MemberExtractResponse.parse(
          await request(base, '/members/extract', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        ),
      listDismissed: async () =>
        DismissedListResponse.parse(await request(base, '/members/dismissed')),
      dismiss: (fingerprints) =>
        request(base, '/members/dismissed', {
          method: 'POST',
          body: JSON.stringify({ fingerprints }),
        }) as Promise<{ added: number }>,
      undismiss: (fingerprints) =>
        request(base, '/members/dismissed', {
          method: 'DELETE',
          body: JSON.stringify({ fingerprints }),
        }) as Promise<{ removed: number }>,
      listFields: async () =>
        MemberFieldListResponse.parse(await request(base, '/members/fields')),
      createField: (input) =>
        request(base, '/members/fields', {
          method: 'POST',
          body: JSON.stringify(input),
        }) as Promise<{ field: MemberFieldDefDTO }>,
      updateField: (id, input) =>
        request(base, `/members/fields/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        }) as Promise<{ field: MemberFieldDefDTO }>,
      removeField: (id) =>
        request(base, `/members/fields/${id}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
    },
    profile: {
      me: async () => ProfileResponse.parse(await request(base, '/profile/me')),
      save: (input) =>
        request(base, '/profile/me', { method: 'PUT', body: JSON.stringify(input) }) as Promise<{ member: MemberDTO }>,
    },
    search: {
      run: async (query) => SearchResponse.parse(await request(base, `/search${buildQuery(query as Record<string, unknown>)}`)),
    },
    matches: {
      list: async (q) => MatchListResponse.parse(await request(base, `/matches${buildQuery(q as Record<string, unknown> | undefined)}`)),
      suggest: async (memberId, opts) => {
        const params = new URLSearchParams({ memberId });
        if (opts?.ai) params.set('ai', '1');
        return MatchSuggestionsResponse.parse(await request(base, `/matches/suggest?${params.toString()}`));
      },
      explain: async (input) =>
        MatchExplainResponse.parse(
          await request(base, '/matches/explain', { method: 'POST', body: JSON.stringify(input) }),
        ),
      create: (input) =>
        request(base, '/matches', { method: 'POST', body: JSON.stringify(input) }) as Promise<{ match: MatchDTO }>,
      update: (id, input) =>
        request(base, `/matches/${id}`, { method: 'PATCH', body: JSON.stringify(input) }) as Promise<{ match: MatchDTO }>,
      remove: (id) => request(base, `/matches/${id}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
    },





    map: {
      get: async () => MapResponse.parse(await request(base, '/map')),
      geoSearch: async (q) =>
        GeoSearchResponse.parse(await request(base, `/map/geo-search?q=${encodeURIComponent(q)}`)),
    },





    account: {
      inviteLink: async () => InviteLinkResponse.parse(await request(base, '/account/invite-link')),
      updateProfile: (input) =>
        request(base, '/account/profile', { method: 'PATCH', body: JSON.stringify(input) }) as Promise<{ user: UserDTO }>,
      changePassword: (input) =>
        request(base, '/account/password', { method: 'POST', body: JSON.stringify(input) }) as Promise<{ ok: true }>,
      deleteAccount: async (input) => {
        await request(base, '/account', { method: 'DELETE', body: JSON.stringify(input) });
      },
      markOnboarded: () =>
        request(base, '/account/onboarded', { method: 'POST' }) as Promise<{ user: UserDTO }>,
    },
    backup: {
      export: async () => BackupExport.parse(await request(base, '/backup/export')),
      import: async (input) =>
        BackupImportResult.parse(
          await request(base, '/backup/import', { method: 'POST', body: JSON.stringify(input) }),
        ),
    },
    analytics: {
      last7Days: async () => AdminAnalytics7d.parse(await request(base, '/analytics/7d')),
      activity: async (days = 14) =>
        ActivityResponse.parse(await request(base, `/analytics/activity?days=${days}`)),
      aiCosts: async (days = 30) =>
        AiCostsResponse.parse(await request(base, `/analytics/ai-costs?days=${days}`)),
    },
    join: {
      host: async (slug) => JoinHostResponse.parse(await request(base, `/join/${slug}`)),
      submit: async (slug, input) =>
        JoinSubmitResponse.parse(
          await request(base, `/join/${slug}`, { method: 'POST', body: JSON.stringify(input) }),
        ),
      connect: async (slug) =>
        JoinConnectResponse.parse(await request(base, `/join/${slug}/connect`, { method: 'POST' })),
    },
    feedback: {
      create: (input) =>
        request(base, '/feedback', { method: 'POST', body: JSON.stringify(input) }) as Promise<{ feedback: FeedbackDTO }>,
      mine: async () => FeedbackListResponse.parse(await request(base, '/feedback/mine')),
      get: async (id) => FeedbackDetailResponse.parse(await request(base, `/feedback/${id}`)),
      remove: (id) => request(base, `/feedback/${id}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
      adminList: async (q) =>
        FeedbackListResponse.parse(
          await request(base, `/admin/feedback${buildQuery(q as Record<string, unknown> | undefined)}`),
        ),
      adminStats: async () => FeedbackStatsResponse.parse(await request(base, '/admin/feedback/stats')),
      adminUpdate: (id, input) =>
        request(base, `/admin/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(input) }) as Promise<{ feedback: FeedbackDTO }>,
    },
    network: {
      createRelationship: (input) =>
        request(base, '/network/relationships', {
          method: 'POST',
          body: JSON.stringify(input),
        }) as Promise<{ relationship: RelationshipDTO }>,
      updateRelationship: (id, input) =>
        request(base, `/network/relationships/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        }) as Promise<{ relationship: RelationshipDTO }>,
      removeRelationship: (id) =>
        request(base, `/network/relationships/${id}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
    },
    groups: {
      list: async () => GroupListResponse.parse(await request(base, '/groups')),
      create: async (input) =>
        GroupDetailResponse.parse(await request(base, '/groups', { method: 'POST', body: JSON.stringify(input) })),
      get: async (id) => GroupDetailResponse.parse(await request(base, `/groups/${id}`)),
      update: async (id, input) =>
        GroupDetailResponse.parse(await request(base, `/groups/${id}`, { method: 'PATCH', body: JSON.stringify(input) })),
      remove: (id) => request(base, `/groups/${id}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
      leave: (id) => request(base, `/groups/${id}/leave`, { method: 'POST' }) as Promise<{ ok: true }>,
      removeMember: (id, userId) =>
        request(base, `/groups/${id}/members/${userId}`, { method: 'DELETE' }) as Promise<{ ok: true }>,
      setRole: async (id, userId, input) =>
        GroupDetailResponse.parse(
          await request(base, `/groups/${id}/members/${userId}`, { method: 'PATCH', body: JSON.stringify(input) }),
        ),
      inviteByEmail: async (id, input) =>
        GroupManualInviteResponse.parse(
          await request(base, `/groups/${id}/members`, { method: 'POST', body: JSON.stringify(input) }),
        ),
      invites: async (id) => GroupInviteListResponse.parse(await request(base, `/groups/${id}/invites`)),
      createInvite: async (id, input) =>
        GroupInviteResponse.parse(
          await request(base, `/groups/${id}/invites`, { method: 'POST', body: JSON.stringify(input) }),
        ),
      revokeInvite: async (id, inviteId) =>
        GroupInviteResponse.parse(await request(base, `/groups/${id}/invites/${inviteId}`, { method: 'DELETE' })),
      memberProfile: async (id, userId) =>
        GroupMemberProfileResponse.parse(await request(base, `/groups/${id}/members/${userId}/profile`)),
      connectMemberProfile: (id, userId) =>
        request(base, `/groups/${id}/members/${userId}/profile/connect`, { method: 'POST' }) as Promise<{
          memberId: string;
          alreadyExists: boolean;
        }>,
      previewInvite: async (token) => GroupInvitePreviewResponse.parse(await request(base, `/group-invites/${token}`)),
      acceptInvite: async (token) =>
        GroupInviteAcceptResponse.parse(await request(base, `/group-invites/${token}/accept`, { method: 'POST' })),
    },
  };
}

export type {
  GroupSummaryDTO,
  GroupDetailDTO,
  GroupMemberDTO,
  GroupMemberProfileDTO,
  GroupMemberProfileResponse,
  GroupInviteDTO,
  GroupInvitePolicy,
  GroupRole,
  GroupInvitePreviewResponse,
} from '@nexply/api-contract';

export type {
  UserDTO,
  LoginInput,
  RegisterInput,
  UpdateUserInput,
  MemberDTO,
  MemberCreateInput,
  MemberUpdateInput,
  MemberListQuery,
  MemberImportRow,
  MemberExtractInput,
  MemberExtractResponse,
  ExtractedMember,
  ExtractedLink,
  SearchQuery,
  SearchResult,
  SearchResponse,
  MatchDTO,
  MatchStatus,
  MatchSuggestionDTO,
  MatchCreateInput,
  MatchUpdateInput,
  MatchListQuery,
  MatchListResponse,
  MatchSuggestionsResponse,
  MatchFactorDTO,
  MatchExplainInput,
  MatchExplanationDTO,
  MatchExplainResponse,
  MapMemberDTO,
  MapWithoutLocationDTO,
  GeoPlaceDTO,
  MapResponse,
  ChangePasswordInput,
  UpdateProfileInput,
  DeleteAccountInput,
  BackupExport,
  BackupData,
  BackupImportInput,
  BackupImportResult,
  AdminAnalytics7d,
  AdminAnalyticsDay,
  AdminAnalyticsTotals,
  AiCostsResponse,
  RelationshipType,
  RelationshipDTO,
  RelationshipCreateInput,
  RelationshipUpdateInput,
  MemberFieldDefDTO,
  MemberFieldType,
  MemberFieldCreateInput,
  MemberFieldUpdateInput,
  MemberFieldListResponse,
  FeedbackDTO,
  FeedbackDetailDTO,
  FeedbackType,
  FeedbackStatus,
  FeedbackPriority,
  FeedbackCreateInput,
  FeedbackUpdateInput,
  FeedbackListQuery,
  FeedbackListResponse,
  FeedbackStatsResponse,
} from '@nexply/api-contract';
